import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import GitHubIcon from '@mui/icons-material/GitHub';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import { personalInfo } from '../utils/constants';
import { SectionHeading, SocialLinks } from './FxSections';

export const NAV_ITEMS = [
    { id: 'about', label: 'About' },
    { id: 'experience', label: 'Experience' },
    { id: 'skills', label: 'Skills' },
    { id: 'projects', label: 'Projects' },
    { id: 'contact', label: 'Contact' },
];

export function Nav({ active, progress }) {
    const [open, setOpen] = useState(false);
    const scrolled = progress > 0.01;

    useEffect(() => {
        if (!open) {
            return undefined;
        }
        const onKey = (event) => {
            if (event.key === 'Escape') {
                setOpen(false);
            }
        };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [open]);

    const close = () => setOpen(false);

    return (
        <header className={`fx-nav${scrolled ? ' is-scrolled' : ''}`}>
            <div
                className="fx-nav__progress"
                style={{ transform: `scaleX(${progress})` }}
                aria-hidden="true"
            />
            <nav className="fx-container fx-nav__inner" aria-label="Primary">
                <a className="fx-logo" href="#home" onClick={close} aria-label="Back to top">
                    <span className="fx-gradient-text">VS</span>
                    <span className="fx-logo__dot" aria-hidden="true">.</span>
                </a>
                <button
                    type="button"
                    className="fx-icon-btn fx-nav__toggle"
                    aria-expanded={open}
                    aria-controls="fx-nav-menu"
                    aria-label={open ? 'Close menu' : 'Open menu'}
                    onClick={() => setOpen((value) => !value)}
                >
                    {open ? <CloseIcon /> : <MenuIcon />}
                </button>
                <ul id="fx-nav-menu" className={`fx-nav__menu${open ? ' is-open' : ''}`}>
                    {NAV_ITEMS.map((item) => (
                        <li key={item.id}>
                            <a
                                href={`#${item.id}`}
                                className={`fx-nav__link${active === item.id ? ' is-active' : ''}`}
                                aria-current={active === item.id ? 'location' : undefined}
                                onClick={close}
                            >
                                {item.label}
                            </a>
                        </li>
                    ))}
                    <li>
                        <a
                            className="fx-btn fx-btn--small fx-btn--primary"
                            href={personalInfo.resumePath}
                            download="Veeramalli_Sathwik_Resume.pdf"
                            onClick={close}
                        >
                            <FileDownloadOutlinedIcon fontSize="inherit" aria-hidden="true" />
                            Résumé
                        </a>
                    </li>
                </ul>
            </nav>
        </header>
    );
}

Nav.propTypes = {
    active: PropTypes.string.isRequired,
    progress: PropTypes.number.isRequired,
};

function CopyEmailButton() {
    const [copied, setCopied] = useState(false);

    useEffect(() => {
        if (!copied) {
            return undefined;
        }
        const timer = setTimeout(() => setCopied(false), 2000);
        return () => clearTimeout(timer);
    }, [copied]);

    const copy = async () => {
        try {
            await navigator.clipboard.writeText(personalInfo.email);
            setCopied(true);
        } catch {
            globalThis.location.href = `mailto:${personalInfo.email}`;
        }
    };

    return (
        <button type="button" className="fx-btn fx-btn--ghost" onClick={copy}>
            {copied ? (
                <CheckIcon fontSize="small" aria-hidden="true" />
            ) : (
                <ContentCopyIcon fontSize="small" aria-hidden="true" />
            )}
            <span aria-live="polite">{copied ? 'Copied!' : 'Copy email'}</span>
        </button>
    );
}

export function Contact() {
    const channels = [
        { label: 'Email', value: personalInfo.email, href: `mailto:${personalInfo.email}`, Icon: EmailOutlinedIcon },
        { label: 'Phone', value: personalInfo.phone, href: `tel:${personalInfo.phone.replaceAll(' ', '')}`, Icon: PhoneOutlinedIcon },
        { label: 'LinkedIn', value: 'veeramalli-sathwik', href: personalInfo.linkedin, Icon: LinkedInIcon },
        { label: 'GitHub', value: 'sathwik22', href: personalInfo.github, Icon: GitHubIcon },
    ];
    return (
        <section id="contact" className="fx-section fx-contact" aria-labelledby="contact-title">
            <div className="fx-container">
                <SectionHeading id="contact-title" index="05" kicker="Contact" title={<>Let&apos;s build something <span className="fx-gradient-text">extraordinary</span></>}>
                    Open to exciting frontend roles, collaborations and
                    conversations about React, performance and GenAI.
                </SectionHeading>
                <div className="fx-contact__grid">
                    {channels.map(({ label, value, href, Icon }) => (
                        <a
                            key={label}
                            className="fx-glass fx-channel"
                            href={href}
                            target={href.startsWith('http') ? '_blank' : undefined}
                            rel="noopener noreferrer"
                            data-reveal
                        >
                            <span className="fx-channel__icon" aria-hidden="true"><Icon /></span>
                            <span className="fx-channel__label">{label}</span>
                            <span className="fx-channel__value">{value}</span>
                        </a>
                    ))}
                </div>
                <div className="fx-contact__cta" data-reveal>
                    <a className="fx-btn fx-btn--primary fx-btn--large" href={`mailto:${personalInfo.email}`}>
                        <EmailOutlinedIcon fontSize="small" aria-hidden="true" />
                        Say hello
                    </a>
                    <CopyEmailButton />
                </div>
            </div>
        </section>
    );
}

export function Footer() {
    return (
        <footer className="fx-footer">
            <div className="fx-container fx-footer__inner">
                <p>
                    © {new Date().getFullYear()} {personalInfo.name}. Crafted with
                    React &amp; three.js.
                </p>
                <SocialLinks />
                <a className="fx-icon-btn" href="#home" aria-label="Back to top">
                    <ArrowUpwardIcon fontSize="small" aria-hidden="true" />
                </a>
            </div>
        </footer>
    );
}
